import type { TID } from "@svar-ui/lib-state";
import type {
	ITask,
	IParsedTask,
	IHistory,
	IData,
	IDataConfig,
	IDataHash,
	ICalendar,
} from "../types";
import { defaultTaskTypes } from "../taskTypes";
// #if [!(WX_PACKAGE_TYPE=open)]
import { isIndivisible } from "../pro/splitTasks";
// #endif

function isGrouped(task: IParsedTask) {
	return !task.$group && typeof task.$groupValue !== "undefined";
}
function isGroup(task: IParsedTask) {
	return task.$group;
}

function isGroupParent(task: IParsedTask) {
	return isGroup(task) && typeof task.$groupValue === "undefined";
}

const groupedMenuOptions = new Set<TID>([
	"add-task",
	"delete-task",
	"edit-task",
	"convert-task",
]);

export function getMenuOptions(config?: IDataConfig & { group?: boolean }) {
	let options = defaultMenuOptions.map(item => ({ ...item }));

	const taskTypes = config?.taskTypes || defaultTaskTypes;
	const convertOption = options.find(o => o.id === "convert-task");

	convertOption.data = [];
	taskTypes.forEach(t => {
		if (!config?.summary?.autoConvert || t.id !== "summary")
			convertOption.data.push(convertOption.dataFactory(t));
	});

	// #if [!(WX_PACKAGE_TYPE=open)]
	if (config?.summary?.autoConvert)
		convertOption.data.forEach(
			o => (o.isHidden = t => t.type === "summary")
		);

	if (config?.splitTasks) {
		options.splice(4, 0, {
			id: "split-task",
			text: "Split",
			icon: "wxi-arrow-split",
		});
		options.push({
			id: "delete",
			icon: "wxi-delete",
			text: "Delete",
			data: [
				{
					id: "delete-task:task",
					text: "Task",
					isHidden: (t, s, target) =>
						!isSingle(s) || !isSegment(target),
				},
				{
					id: "delete-task:segment",
					text: "Segment",
					isHidden: (t, s, target) =>
						!isSingle(s) || !isSegment(target),
				},
			],
		});
	}

	if (config?.group) {
		options = options.filter(
			op => op.comp === "separator" || groupedMenuOptions.has(op.id)
		);
	}

	if (options.length > defaultMenuOptions.length || config?.group)
		options = assignChecks(options);
	// #endif
	return options;
}

export interface IOptionConfig {
	id?: TID;
	separator?: boolean;
	text?: string;
	icon?: string;
	data?: IOptionConfig[];
	comp?: string;
	isDisabled?:
		| ((
				task: IParsedTask,
				state?: IData,
				calendar?: ICalendar,
				target?: TID | IDataHash
		  ) => boolean)
		| ((history: IHistory) => boolean);
	dataFactory?: (obj: any) => IOptionConfig;
	subtext?: string;
	isHidden?: (
		task: IParsedTask,
		state: IData,
		target?: TID | IDataHash
	) => boolean;
}

export function assignChecks<T extends IOptionConfig>(items: T[]): T[] {
	return items.map(item => {
		if (item.data) assignChecks(item.data);
		switch (item.id) {
			case "add-task:before":
			case "move-task:up":
				item.isDisabled = (task, state) =>
					isFirstTask(task, state) ||
					isGrouped(task) ||
					isGroup(task);
				break;
			case "move-task:down":
				item.isDisabled = (task, state) => isLastTask(task, state);
				break;
			case "indent-task:add":
				item.isDisabled = (task, state) =>
					prevTaskID(task, state) === task.parent;
				break;
			case "indent-task:remove":
				item.isDisabled = (task: IParsedTask) => isRootTask(task);
				break;
			// #if [!(WX_PACKAGE_TYPE=open)]
			case "add-task":
				item.isDisabled = (task: IParsedTask) =>
					isGrouped(task) || isGroupParent(task);
				break;
			case "add-task:child":
				item.isDisabled = (task: IParsedTask) =>
					isGrouped(task) || isGroupParent(task);
				break;
			case "add-task:after":
				item.isDisabled = (task: IParsedTask) =>
					isGrouped(task) || isGroup(task);
				break;
			case "edit-task":
			case "delete-task":
			case "convert-task":
				item.isDisabled = (task: IParsedTask) => isGroup(task);
				break;
			case "undo":
				item.isDisabled = (history: IHistory) => !history.undo;
				break;
			case "redo":
				item.isDisabled = (history: IHistory) => !history.redo;
				break;
			case "split-task":
				item.isDisabled = (task, state, calendar, target) => {
					let index;
					if (target && typeof target === "object")
						index = target.segmentIndex;
					return isIndivisible(task, state, calendar, index);
				};
				break;
			// #endif
		}

		return item;
	});
}

function isRootTask(task: ITask) {
	return task.parent === 0;
}

function isFirstTask(task: ITask, state: IData): boolean {
	const { _tasks } = state;
	return _tasks[0]?.id === task.id;
}
function isLastTask(task: ITask, state: IData): boolean {
	const { _tasks } = state;
	return _tasks[_tasks.length - 1]?.id === task.id;
}
function prevTaskID(task: ITask, state: IData): TID {
	const { _tasks } = state;
	const taskIndex = _tasks.findIndex(t => t.id === task.id);
	return _tasks[taskIndex - 1]?.id ?? task.parent;
}

function isSegment(target: TID | IDataHash): boolean {
	return target && typeof target === "object";
}
function isSingle(s: IData): boolean {
	return !s.selected || s.selected.length < 2;
}

const exclude = (v: any) => (task: IParsedTask) =>
	task.type === v || (v === "summary" && isGrouped(task));

export const defaultMenuOptions: IOptionConfig[] = assignChecks<IOptionConfig>([
	{
		id: "add-task",
		text: "Add",
		icon: "wxi-plus",
		data: [
			{
				id: "add-task:child",
				text: "Child task",
			},
			{
				id: "add-task:before",
				text: "Task above",
			},
			{
				id: "add-task:after",
				text: "Task below",
			},
		],
	},
	{ comp: "separator" },
	{
		id: "convert-task",
		text: "Convert to",
		icon: "wxi-swap-horizontal",
		dataFactory: type => ({
			id: `convert-task:${type.id}`,
			text: `${type.label}`,
			isDisabled: exclude(type.id),
		}),
	},
	{
		id: "edit-task",
		text: "Edit",
		icon: "wxi-edit",
	},
	{ comp: "separator" },
	{ id: "cut-task", text: "Cut", icon: "wxi-content-cut", subtext: "Ctrl+X" },
	{
		id: "copy-task",
		text: "Copy",
		icon: "wxi-content-copy",
		subtext: "Ctrl+C",
	},
	{
		id: "paste-task",
		text: "Paste",
		icon: "wxi-content-paste",
		subtext: "Ctrl+V",
	},
	{
		id: "move-task",
		text: "Move",
		icon: "wxi-swap-vertical",
		data: [
			{ id: "move-task:up", text: "Up" },
			{ id: "move-task:down", text: "Down" },
		],
	},
	{ comp: "separator" },
	{ id: "indent-task:add", text: "Indent", icon: "wxi-indent" },
	{ id: "indent-task:remove", text: "Outdent", icon: "wxi-unindent" },
	{ comp: "separator" },
	{
		id: "delete-task",
		icon: "wxi-delete",
		text: "Delete",
		subtext: "Ctrl+D / BS",
		isHidden: (t, s, target) => isSingle(s) && isSegment(target),
	},
]);
